import {
  MD2Theme,
  MD2DarkTheme as PaperDefaultDarkTheme,
  MD2LightTheme as PaperDefaultLightTheme,
} from 'react-native-paper';

import {
  DarkTheme as NavigationDefaultDarkTheme,
  DefaultTheme as NavigationDefaultLightTheme,
} from '@react-navigation/native';
import type { Theme as NavigationTheme } from '@react-navigation/native';

export enum ThemeToggle {
  LIGHT = 'LIGHT',
  DARK = 'DARK',
}

const ROUNDNESS = 20;
const MAX_WIDTH = 512;
const COLOR_PRIMARY = '#d9ad57';

type AppThemeColors = Pick<
  MD2Theme['colors'],
  'accent' | 'background' | 'disabled' | 'error' | 'placeholder' | 'primary' | 'surface' | 'text'
> &
  Pick<NavigationTheme['colors'], 'border' | 'card' | 'notification'>;

type AppThemeFonts = Pick<MD2Theme['fonts'], 'medium' | 'regular'>;

export type AppTheme = {
  /** True when the selected app theme uses dark colors. */
  dark: boolean;
  /** Shared border radius used by app surfaces and Paper components. */
  roundness: number;
  /** Maximum content width for centered app layouts. */
  maxWidth: number;
  /** App color tokens for non-provider UI. */
  colors: AppThemeColors;
  /** Text font variants used by app typography. */
  fonts: AppThemeFonts;
};

export const AppLightTheme: AppTheme = {
  dark: false,
  roundness: ROUNDNESS,
  maxWidth: MAX_WIDTH,
  colors: {
    accent: 'black',
    background: PaperDefaultLightTheme.colors.background,
    border: NavigationDefaultLightTheme.colors.border,
    card: NavigationDefaultLightTheme.colors.card,
    disabled: PaperDefaultLightTheme.colors.disabled,
    error: PaperDefaultLightTheme.colors.error,
    notification: NavigationDefaultLightTheme.colors.notification,
    placeholder: PaperDefaultLightTheme.colors.placeholder,
    primary: COLOR_PRIMARY,
    surface: PaperDefaultLightTheme.colors.surface,
    text: PaperDefaultLightTheme.colors.text,
  },
  fonts: {
    medium: PaperDefaultLightTheme.fonts.medium,
    regular: PaperDefaultLightTheme.fonts.regular,
  },
};

export const AppDarkTheme: AppTheme = {
  dark: true,
  roundness: ROUNDNESS,
  maxWidth: MAX_WIDTH,
  colors: {
    accent: 'white',
    background: PaperDefaultDarkTheme.colors.background,
    border: NavigationDefaultDarkTheme.colors.border,
    card: NavigationDefaultDarkTheme.colors.card,
    disabled: PaperDefaultDarkTheme.colors.disabled,
    error: PaperDefaultDarkTheme.colors.error,
    notification: NavigationDefaultDarkTheme.colors.notification,
    placeholder: PaperDefaultDarkTheme.colors.placeholder,
    primary: COLOR_PRIMARY,
    surface: PaperDefaultDarkTheme.colors.surface,
    text: PaperDefaultDarkTheme.colors.text,
  },
  fonts: {
    medium: PaperDefaultDarkTheme.fonts.medium,
    regular: PaperDefaultDarkTheme.fonts.regular,
  },
};

export function getAppTheme(theme: ThemeToggle) {
  return theme === ThemeToggle.LIGHT ? AppLightTheme : AppDarkTheme;
}

function createPaperTheme(defaultTheme: MD2Theme, appTheme: AppTheme): MD2Theme {
  return {
    ...defaultTheme,
    roundness: appTheme.roundness,
    colors: {
      ...defaultTheme.colors,
      accent: appTheme.colors.accent,
      primary: appTheme.colors.primary,
    },
  };
}

export const PaperLightTheme = createPaperTheme(PaperDefaultLightTheme, AppLightTheme);

export const PaperDarkTheme = createPaperTheme(PaperDefaultDarkTheme, AppDarkTheme);

function createNavigationTheme(defaultTheme: NavigationTheme, appTheme: AppTheme): NavigationTheme {
  return {
    ...defaultTheme,
    dark: appTheme.dark,
    colors: {
      ...defaultTheme.colors,
      primary: appTheme.colors.primary,
    },
  };
}

export function getPaperTheme(theme: ThemeToggle) {
  return theme === ThemeToggle.LIGHT ? PaperLightTheme : PaperDarkTheme;
}

export const NavigationLightTheme = createNavigationTheme(
  NavigationDefaultLightTheme,
  AppLightTheme
);

export const NavigationDarkTheme = createNavigationTheme(NavigationDefaultDarkTheme, AppDarkTheme);

export function getNavigationTheme(theme: ThemeToggle) {
  return theme === ThemeToggle.LIGHT ? NavigationLightTheme : NavigationDarkTheme;
}
